import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "../customAxios.js";
import * as XLSX from "xlsx";
import {
  Button,
  CircularProgress,
  FormControl,
  Grid,
  Paper,
  Chip,
  Stack,
  TextField,
} from "@mui/material";
import { getImagesUrls } from "../utils/s3Utils.js";
import { regionsAndCommunes } from "../helpers/ChileanAddressesData";
import { orderTypeEnum } from "../helpers/enums";
import LoadingComponent from "../components/LoadingComponent";
import AlertBox from "../components/AlertBox";
import CustomDropzone from "../components/CustomDropzone";
import { useImagePreloader } from "../hooks/useImagePreLoader.jsx";
import { useAuth } from "../hooks/useAuth.jsx";

//GET URL PRESIGNED OF IMAGES
//dict with images key routes of the page
const imageskeyRoutes = {
  s3massiveUploadIcon: "assets/icons/massive-upload.png",
  s3manualUploadIcon: "assets/icons/manual-upload.png",
};

const imageUrls = await getImagesUrls(imageskeyRoutes, "uploadRequests");

function UploadProducts() {
  const { user } = useAuth();
  const [selectedFileType, setSelectedFileType] = useState("");
  const [files, setFiles] = useState([]);
  const [openFileErrorAlert, setOpenFileErrorAlert] = useState(false);
  const [validFile, setValidFile] = useState(false);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);

  // Manual form
  const [sku, setSku] = useState("");
  const [name, setName] = useState("");
  const [price, setPrice] = useState(null);
  const [dimensions, setDimensions] = useState("");
  const [weight, setWeight] = useState(null);
  const [barcode, setBarcode] = useState("");
  const [submitPressed, setSubmitPressed] = useState(false);
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);
  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [messageAlert, setMessageAlert] = useState("");

  const { allImagesLoaded, timeoutReached } = useImagePreloader(
    imageUrls,
    3000
  );

  //get user type
  const isOperator = user.user_type.includes("operator");
  const isSeller = user.user_type.includes("seller");

  let navigate = useNavigate();

  const regionsOptions = regionsAndCommunes.map((region) => ({
    value: region.value,
    label: region.label,
  }));

  const orderTypeOptions = orderTypeEnum.map((orderType) => ({
    value: orderType.value,
    label: orderType.label,
  }));

  //FILE VALIDATION FUNCTIONS

  //this consider the first file on the files list, for now we are only allowing one file to be uploaded
  useEffect(() => {
    setFile(files[0]);
  }, [files]);

  async function uploadProductsExcel() {
    const selectedFile = file;
    var formData = new FormData();
    formData.append("excel_file", selectedFile);
    try {
      await axios.post("products/upload_excel_products_frontend/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMessageAlert("Archivo cargado con éxito");
      setOpenSuccessAlert(true);
    } catch (error) {
      let errorMessage = "Error al cargar el archivo";
      if (
        error?.response &&
        error?.response?.data &&
        error?.response?.data?.error
      ) {
        errorMessage += ": " + error.response.data.error;
      }
      setMessageAlert(errorMessage);
      setOpenErrorAlert(true);
    } finally {
      setFiles([]);
      setFile(null);
      for (let key in localStorage) {
        if (key.startsWith("inventory-seller-")) {
          localStorage.removeItem(key);
        }
      }
    }
  }

  useEffect(() => {
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        //get the two return values of function validateExcel
        const [isValid, message] = validateExcel(json);
        setMessageAlert(message);

        if (isValid) {
          console.log("File is valid");
          setValidFile(true);
        } else {
          console.error("File is invalid");
          setOpenFileErrorAlert(true);
          setValidFile(false);
          setFiles([]);
        }
      };
      reader.readAsBinaryString(file);
    }
  }, [file]); // Dependency array to re-run the effect when `file` changes

  //functions to validate excel file to load massive orders
  const validateExcel = (data) => {
    const requiredHeaders = [
      "Name",
      "Price",
      "SKU",
      "Dimensions",
      "Weight",
      "Barcode",
    ];

    if (data.length === 0 || !data[0]) {
      return [false, "Archivo vacío, no se entregaron productos a cargar."];
    }

    const headers = Object.keys(data[0]);
    const hasRequiredHeaders = requiredHeaders.every((header) =>
      headers.includes(header)
    );
    if (!hasRequiredHeaders) {
      console.log("Missing at least one required header");
      return [false, "Missing at least one required header"];
    }

    for (const row of data) {
      //if price exists, validate it is a number
      if (row["Price"] && isNaN(row["Price"])) {
        console.log("Attributo Price inválido");
        return [
          false,
          `Attributo Price inválido en fila ${data.indexOf(row) + 2}`,
        ];
      }

      //if weight exists, validate it is a number
      if (row["Weight"] && isNaN(row["Weight"])) {
        console.log("Attributo Weight inválido");
        return [
          false,
          `Attributo Weight inválido en fila ${data.indexOf(row) + 2}`,
        ];
      }

      // Validate Name exists
      if (!row["Name"]) {
        console.log("Attributo Name inválido");
        return [
          false,
          `Attributo Name inválido en fila ${data.indexOf(row) + 2}`,
        ];
      }
      // Vaalidate if SKU exists
      if (!row["SKU"]) {
        console.log("Attributo SKU inválido");
        return [
          false,
          `Attributo SKU inválido en fila ${data.indexOf(row) + 2}`,
        ];
      }

      // Validate if Barcode exists
      if (!row["Barcode"]) {
        console.log("Attributo Barcode inválido");
        return [
          false,
          `Attributo Barcode inválido en fila ${data.indexOf(row) + 2}`,
        ];
      }
    }
    return [true, "File is valid"];
  };

  const serializeProducts = (productsData) => {
    const serializedProducts = productsData.map((product) => ({
      sku: product.sku,
      quantity: product.quantity,
    }));
    return serializedProducts;
  };

  async function handleSubmit() {
    setSubmitPressed(true);
    if (
      selectedFileType === "Carga manual" &&
      sku !== "" &&
      name !== "" &&
      barcode !== ""
    ) {
      setLoading(true);
      try {
        await axios.post("products/create_product/", {
          sku: sku,
          name: name,
          dimensions: dimensions,
          price: price,
          weight: weight,
          barcode: barcode,
        });
        setLoading(false);
        setMessageAlert("Producto creado con éxito");
        setOpenSuccessAlert(true);
        clearData();
        navigate("/home/upload-success-product");
      } catch (error) {
        console.error(`Error al crear el producto: ${error}`);
        setLoading(false);
        setMessageAlert("Error al crear el producto");
        setOpenErrorAlert(true);
      } finally {
        for (let key in localStorage) {
          if (key.startsWith("inventory-seller-")) {
            localStorage.removeItem(key);
          }
        }
      }
    }
  }

  async function handleFileSubmit() {
    uploadProductsExcel();
  }

  const handleDelete = (path) => {
    setFiles(files.filter((file) => file.path !== path));
  };

  const clearData = () => {
    setFiles([]);
    setSku("");
    setName("");
    setDimensions("");
    setWeight(null);
    setBarcode("");
    setPrice(null);
    setSubmitPressed(false);
  };

  const handleFileDrop = (acceptedFiles) => {
    // Manejar los archivos aquí
    console.log("Archivos subidos:", acceptedFiles);
    acceptedFiles.forEach((file) => {
      setFiles((prevFiles) => {
        return [...prevFiles, file];
      });
    });
  };

  return (
    <>
      {!allImagesLoaded && !timeoutReached ? (
        <LoadingComponent />
      ) : (
        <>
          <Grid item xs={12}>
            <h1>Carga de Productos</h1>
          </Grid>
          <Grid item xs={12}>
            <FormControl>
              <Link to="/home/seller-inventory" className="back-login-btn">
                <i className="material-icons-outlined forgotten-back-arrow">
                  arrow_back
                </i>
                <p>Volver atrás</p>
              </Link>
            </FormControl>
          </Grid>
          <Grid
            container
            className="select-ecommerce-container"
            style={{ marginBottom: "15px" }}
          >
            <Grid item xs={12} sm={12} className="flex-center">
              <h5>Selecciona el tipo de carga</h5>
            </Grid>
            <Grid sx={{ flexGrow: 1 }} container>
              <Grid item xs={12}>
                <Grid container justifyContent="center">
                  <Grid item className="option first-row">
                    <Paper
                      elevation={0}
                      className={`select-option ${
                        selectedFileType === "Carga masiva" ? "selected" : ""
                      }`}
                      onClick={() => {
                        clearData();
                        setSelectedFileType("Carga masiva");
                      }}
                    >
                      <img
                        src={imageUrls.s3massiveUploadIcon}
                        alt="carga masiva"
                      />
                      <p>Carga masiva</p>
                    </Paper>
                  </Grid>
                  <Grid item className="option second-row">
                    <Paper
                      elevation={0}
                      className={`select-option ${
                        selectedFileType === "Carga manual" ? "selected" : ""
                      }`}
                      onClick={() => {
                        clearData();
                        setSelectedFileType("Carga manual");
                      }}
                    >
                      <img
                        src={imageUrls.s3manualUploadIcon}
                        alt="carga manual"
                      />
                      <p>Carga manual</p>
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            {(selectedFileType === "Carga masiva" ||
              selectedFileType === "Carga manual") && (
              <Grid container className="container-upload-files">
                <Grid item xs={12} className="flex-center">
                  <h5>Carga tu producto</h5>
                </Grid>
                <Grid item xs={12} className="flex-center">
                  <p className="mg-0">
                    {"Seleccionaste hacer una carga "}
                    <span className="fw-medium">{selectedFileType}</span>
                  </p>
                </Grid>
                {selectedFileType === "Carga masiva" && (
                  <Grid item xs={12} className="flex-center download-container">
                    <p>{"Si no tienes a mano el archivo, puedes "}</p>
                    <a
                      href={`https://go-ex-v2-public.s3.amazonaws.com/templates/planilla_ejemplo_carga_masiva_productos.xlsx`}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                    >
                      <p>{" descargar la plantilla"}</p>
                    </a>
                  </Grid>
                )}
              </Grid>
            )}

            <Grid container className="container-upload-files">
              {selectedFileType === "Carga manual" ? (
                <Grid container>
                  <Grid item xs={12} className="flex-center">
                    <Grid container className="manual-upload-container">
                      <Grid item xs={12} sm={6} className="pd-r-10">
                        <TextField
                          fullWidth
                          variant="outlined"
                          label="sku*"
                          name="sku"
                          value={sku}
                          error={submitPressed && sku === ""}
                          helperText={
                            submitPressed && sku === ""
                              ? "Campo obligatorio"
                              : ""
                          }
                          onChange={(e) => setSku(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} className="pd-l-10">
                        <TextField
                          fullWidth
                          error={submitPressed && name === ""}
                          variant="outlined"
                          label="Nombre*"
                          name="name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          helperText={
                            submitPressed && name === ""
                              ? "Campo obligatorio"
                              : ""
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} className="pd-r-10">
                        <TextField
                          fullWidth
                          variant="outlined"
                          label="Precio"
                          name="price"
                          value={price}
                          onChange={(e) => setPrice(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} className="pd-l-10">
                        <TextField
                          fullWidth
                          variant="outlined"
                          label="Dimensiones"
                          name="dimensions"
                          value={dimensions}
                          onChange={(e) => setDimensions(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} className="pd-r-10">
                        <TextField
                          fullWidth
                          variant="outlined"
                          label="Peso"
                          name="weight"
                          value={weight}
                          onChange={(e) => setWeight(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} className="pd-r-10">
                        <TextField
                          fullWidth
                          error={submitPressed && barcode === ""}
                          variant="outlined"
                          label="Código de barras*"
                          name="barcode"
                          value={barcode}
                          onChange={(e) => setBarcode(e.target.value)}
                          helperText={
                            submitPressed && barcode === ""
                              ? "Campo obligatorio"
                              : ""
                          }
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              ) : selectedFileType === "Carga masiva" ? (
                <>
                  <Grid item xs={12} className="flex-center">
                    <CustomDropzone
                      accept={".xlsx"}
                      width={"550px"}
                      marginTop={"50px"}
                      onDrop={handleFileDrop}
                    />
                  </Grid>
                  <Grid item xs={12} className="flex-center">
                    <Grid item className="custom-stack-container">
                      <Stack
                        direction="row"
                        spacing={1}
                        className="custom-stack"
                      >
                        {files.map((file, index) => {
                          return (
                            <Chip
                              key={index}
                              label={file.path}
                              onDelete={() => handleDelete(file.path)}
                            />
                          );
                        })}
                      </Stack>
                    </Grid>
                  </Grid>
                </>
              ) : null}
              <Grid item xs={12} sm={12} className="flex-center">
                {selectedFileType === "Carga manual" && (
                  <Button
                    type="submit"
                    variant="contained"
                    onClick={handleSubmit}
                  >
                    {loading ? (
                      <CircularProgress className="loading-btn" />
                    ) : (
                      "Cargar orden"
                    )}
                  </Button>
                )}
                {selectedFileType === "Carga masiva" && (
                  <Button
                    type="submit"
                    variant="contained"
                    onClick={handleFileSubmit}
                    disabled={!validFile}
                  >
                    {loading ? (
                      <CircularProgress className="loading-btn" />
                    ) : (
                      "Cargar archivo"
                    )}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Grid>
          <AlertBox
            severity="success"
            messageAlert={messageAlert}
            setOpenAlert={setOpenSuccessAlert}
            openAlert={openSuccessAlert}
            hideDuration={4000}
          />
          <AlertBox
            severity="error"
            messageAlert={messageAlert}
            setOpenAlert={setOpenErrorAlert}
            openAlert={openErrorAlert}
            hideDuration={4000}
          />
          <AlertBox
            severity="error"
            messageAlert={messageAlert}
            setOpenAlert={setOpenFileErrorAlert}
            openAlert={openFileErrorAlert}
            hideDuration={4000}
          />
        </>
      )}
    </>
  );
}

export default UploadProducts;
